import React, { useState, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { LinearProgress, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, Button } from '@mui/material'
import SourceApi from '../../../../api/SourceApi'
import { getValidSourceTypes, getValidUploadedSourceTypes } from '../../../../utils/issues/sourceTypes'
import { intersection } from './utils'
import { IssueType } from 'types/IssueEntity'
import { WarrantEntity } from 'types/WarrantEntity'
import { SourceEntity } from 'types/SourceEntity'
import { UploadedSourceDialog } from './AdminUploadedSourceDialog'
import { SourceForm, SourceFormRow } from './AdminSourceFormRow'
import { SourceTableRow } from './AdminSourceTableRows'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { isBefore } from 'date-fns'
import { FormIssueEntity } from '../../issues/issue-form/AdminIssueForm'

const allowUploadedSource = (issueType: IssueType | 'warrant') => {
    return intersection(getValidUploadedSourceTypes(), getValidSourceTypes(issueType)).length > 0
}

const nullEmptyStrings = (obj: Record<string, unknown>) => {
    return Object.keys(obj).reduce(
        (acc, key) => ({
            ...acc,
            [key]: obj[key] === '' ? null : obj[key],
        }),
        {},
    )
}

function SourcesFields({
    isIssue,
    warrant,
    convertible,
    allowUpload = true,
    setIsCustomFormDirty,
}: {
    isIssue?: boolean
    warrant?: WarrantEntity
    convertible?: ConvertibleEntity
    allowUpload?: boolean
    setIsCustomFormDirty: (val: boolean) => void
}) {
    const [duplicateSources, setDuplicateSources] = useState({})
    const { setValue, watch } = useFormContext<FormIssueEntity | WarrantEntity | ConvertibleEntity>()
    const entity = watch()

    const sources = entity.sources
    const [openUploadDialog, setOpenUploadDialog] = useState(false)
    const sourceApi = new SourceApi()

    const setFormSources = useCallback(
        (sources: Array<Omit<SourceEntity, 'id'>>) => {
            setValue('sources', sources)
        },
        [setValue],
    )

    if (!sources) return <LinearProgress />

    const handleSubmit = (sourceForm: SourceForm) => {
        const source = nullEmptyStrings(sourceForm) as Omit<SourceEntity, 'id'>
        if (isIssue) source.issueId = entity.id
        if (warrant) source.warrantId = warrant.id
        if (convertible) source.convertibleId = convertible.id
        const sourceAlreadyExists = sources.some((s) => s.sourceUrl === source.sourceUrl)
        if (sourceAlreadyExists) {
            console.error('Source already added')
            return
        }
        setIsCustomFormDirty(true)
        setFormSources([source, ...sources])

        // it is possible to add a source without a sourceUrl, but then we don't check for duplicates
        if (source.sourceUrl) {
            sourceApi
                .findBySourceUrl(source.sourceUrl)
                .then((sources) => {
                    console.log({ result: sources })
                    if (sources.length > 0) {
                        // result is a set of sources with the same sourceUrl
                        setDuplicateSources({ ...duplicateSources, [source.sourceUrl]: sources })
                    }
                })
                .catch((err) => console.error(err))
        }
    }
    const handleUploadSubmit = async (uploadUrl: string, file: File, data: SourceForm) => {
        if (!allowUpload) return
        const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/pdf' },
            body: file,
        })
        if (uploadResponse.ok) {
            console.log('Upload successful')
            void handleSubmit(data)
        } else {
            console.error('Upload failed')
        }
    }
    allowUpload = allowUpload && allowUploadedSource(entity?.type)
    return (
        <React.Fragment>
            {allowUpload && (
                <>
                    <UploadedSourceDialog
                        open={openUploadDialog}
                        setOpen={setOpenUploadDialog}
                        handleSubmit={handleUploadSubmit}
                        issue={isIssue ? entity : null}
                        publicationDate={entity?.publicationDate || warrant?.publicationDate || null}
                        subStartDate={watch('subStartDate')}
                        warrant={warrant}
                    />
                    <Button variant='outlined' onClick={() => setOpenUploadDialog(true)}>
                        Ny uppladdad källa
                    </Button>
                </>
            )}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>URL</TableCell>
                            <TableCell>Kommentar</TableCell>
                            <TableCell>Typ</TableCell>
                            <TableCell>Titel</TableCell>
                            <TableCell>Publicerad</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <SourceFormRow
                            onSubmit={handleSubmit}
                            issue={isIssue ? entity : null}
                            warrant={warrant}
                            convertible={convertible}
                            sources={sources}
                        />
                        {sources
                            .sort((a, b) => (isBefore(new Date(a.publishedDate), new Date(b.publishedDate)) ? -1 : 1))
                            .map((source) => (
                                <SourceTableRow
                                    key={source.id || source.sourceUrl}
                                    source={source}
                                    sources={sources}
                                    setSources={setFormSources}
                                    duplicateSources={duplicateSources}
                                    issue={isIssue ? entity : null}
                                    warrant={warrant}
                                    convertible={convertible}
                                />
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </React.Fragment>
    )
}

export { SourcesFields }
